import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useContracts, useClients, useTarifas, useComisionConfig, useConvenios } from "../../contexts/DataContext";
import { Heading } from "../../components/heading";
import { Text } from "../../components/text";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/table";
import { Badge } from "../../components/badge";
import { Listbox, ListboxOption, ListboxLabel } from "../../components/listbox";
import { FianzaView } from "../../components/FianzaView";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  getFechaEmision,
  calculateTarifa,
  getEmisorNombre,
  getSubRamo,
  getTipoFianza,
  toTimestamp,
  formatNumeroFianza,
} from "../../utils/calculations";
import { useComisionLedger } from "../../hooks/useComisionLedger";
import { indexMovementsByFianza, resolveComision } from "../../utils/comisionReconcile";

const monthNames = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function MonthlyProduction({ selectedYear }) {
  const { contractsList, contractsLoading } = useContracts();
  const { clientsById, clientsLoading } = useClients();
  const { tarifas, tarifasLoading } = useTarifas();
  const { movements } = useComisionLedger();
  const { config: comisionConfig } = useComisionConfig();
  const { conveniosById } = useConvenios();
  const byFianza = useMemo(() => indexMovementsByFianza(movements), [movements]);
  const now = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedEmisor, setSelectedEmisor] = useState("all");
  const [selectedFianza, setSelectedFianza] = useState(null);
  const [isFianzaOpen, setIsFianzaOpen] = useState(false);

  const allFianzas = useMemo(() => {
    return contractsList.flatMap((contract) => {
      // Convenio-split context so the on-the-fly fallback nets the same way
      // the ledger books (see comisionReconcile.resolveComision).
      const splitOpts = {
        contractData: contract,
        orgConfig: comisionConfig,
        convenioData: contract.convenioId ? conveniosById?.[contract.convenioId] : null,
      };
      const clientName = clientsById[contract.clientId]?.nombreEmpresa || "Cliente sin nombre";
      const contratoTitle = contract.tituloContrato?.value || "Sin título";

      return (contract.fianzas || [])
        .filter((fianza) => fianza.estadoFianza !== 'anulada' && fianza.estadoFianza !== 'sustituida') // anulada + sustituida excluded; cancelada retained
        .map((fianza) => {
        const inicio = getFechaEmision(fianza);
        const montoAfianzado = Number(fianza.montoAfianzado) || 0;
        const vigencia = { inicio: toTimestamp(fianza.inicioVigenciaFianza), fin: toTimestamp(fianza.finVigenciaFianza) };
        const emisorNombre = getEmisorNombre(fianza);
        const fianzaSubRamo = getSubRamo(fianza);
        const fianzaTipo = getTipoFianza(fianza);

        // Ledger-net prima AND comisión when the fianza has movements; on-the-fly
        // otherwise. Taking `prima` from here (rather than recomputing it) is what
        // keeps the prima column consistent with the comisión next to it after an
        // endoso monetario.
        const {
          prima: primaAmount,
          comisionPct,
          comisionMonto,
          primaMinima,
          tarifa: fianzaTarifa,
        } = resolveComision(
          fianza, byFianza.get(fianza.id), tarifas, emisorNombre, fianzaSubRamo, fianzaTipo, splitOpts
        );
        const tarifaCalculada = calculateTarifa(primaAmount, montoAfianzado, { vigencia });

        return {
          ...fianza,
          clientId: contract.clientId,
          contratoId: contract.id,
          contratoTitle,
          clientName,
          inicioVigenciaMs: inicio,
          montoAfianzado: montoAfianzado,
          primaAmount,
          tarifaCalculada,
          emisorNombre,
          fianzaTarifa,
          primaMinima,
          comisionPct,
          comisionMonto,
        };
      });
    });
  }, [contractsList, clientsById, tarifas, byFianza, comisionConfig, conveniosById]);

  const emisoresList = useMemo(() => {
    const set = new Set();
    allFianzas.forEach((f) => {
      if (f.emisorNombre) set.add(f.emisorNombre);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [allFianzas]);

  const yearFiltered = useMemo(() => {
    const result = allFianzas.filter((f) => {
      if (!f.inicioVigenciaMs) return false;
      const yr = new Date(f.inicioVigenciaMs).getFullYear();
      const emisorMatch = selectedEmisor === "all" || f.emisorNombre === selectedEmisor;
      return yr === selectedYear && emisorMatch;
    });
    return result;
  }, [allFianzas, selectedYear, selectedEmisor]);

  const monthlyFianzas = useMemo(() => {
    const result = yearFiltered.filter((f) => {
      const m = new Date(f.inicioVigenciaMs).getMonth();
      return m === selectedMonth;
    });
    return result;
  }, [yearFiltered, selectedMonth]);

  const filteredFianzas = monthlyFianzas;

  const totalPrima = filteredFianzas.reduce(
    (sum, fianza) => sum + (Number(fianza.primaAmount) || 0),
    0
  );
  const totalComision = filteredFianzas.reduce(
    (sum, fianza) => sum + (Number(fianza.comisionMonto) || 0),
    0
  );

  const yearlyTotals = useMemo(() => {
    const monto = yearFiltered.reduce((sum, f) => sum + (Number(f.montoAfianzado) || 0), 0);
    const prima = yearFiltered.reduce((sum, f) => sum + (Number(f.primaAmount) || 0), 0);
    const comision = yearFiltered.reduce((sum, f) => sum + (Number(f.comisionMonto) || 0), 0);
    return { monto, prima, comision, count: yearFiltered.length };
  }, [yearFiltered]);

  const monthlyTotalsForYear = useMemo(() => {
    const base = Array.from({ length: 12 }, () => ({
      monto: 0,
      prima: 0,
      comision: 0,
      count: 0,
    }));
    yearFiltered.forEach((f) => {
      if (!f.inicioVigenciaMs) return;
      const m = new Date(f.inicioVigenciaMs).getMonth();
      base[m].monto += Number(f.montoAfianzado) || 0;
      base[m].prima += Number(f.primaAmount) || 0;
      base[m].comision += Number(f.comisionMonto) || 0;
      base[m].count += 1;
    });
    return base;
  }, [yearFiltered]);

  // Chart data for commission per month
  const chartData = useMemo(() => {
    return monthlyTotalsForYear.map((item, idx) => ({
      month: monthNames[idx].substring(0, 3).charAt(0).toUpperCase() + monthNames[idx].substring(1, 3),
      comision: item.comision,
      prima: item.prima,
    }));
  }, [monthlyTotalsForYear]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(value);

  const loading = contractsLoading || clientsLoading || tarifasLoading;

  const tabs = [
    { id: "anual", label: "Resumen anual" },
    { id: "mensual", label: "Producción mensual" },
  ];
  const [activeTab, setActiveTab] = useState("anual");

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Heading level={1} className="text-3xl font-bold text-azul-surelia">
            Reporte de producción
          </Heading>
          <Badge color="blue">{selectedYear}</Badge>
          <div className="flex items-center gap-2 ml-auto" data-pdf-hide>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="text-sm border border-azul-surelia/20 rounded-lg px-3 py-2"
            >
              {monthNames.map((name, idx) => (
                <option key={name} value={idx}>
                  {name.charAt(0).toUpperCase() + name.slice(1)}
                </option>
              ))}
            </select>
            <div className="min-w-[200px]">
              <Listbox value={selectedEmisor} onChange={setSelectedEmisor}>
                <ListboxOption value="all">
                  <ListboxLabel>Todos los emisores</ListboxLabel>
                </ListboxOption>
                {emisoresList.map((name) => (
                  <ListboxOption key={name} value={name}>
                    <ListboxLabel>{name}</ListboxLabel>
                  </ListboxOption>
                ))}
              </Listbox>
            </div>
          </div>
        </div>
        <Text className="text-azul-surelia/70">
          Fianzas con inicio de vigencia en el periodo seleccionado.
        </Text>
      </div>

      <div className="flex flex-wrap gap-2" data-pdf-hide>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg border transition ${
              activeTab === tab.id
                ? "bg-azul-surelia text-white border-azul-surelia"
                : "bg-white text-azul-surelia border-azul-surelia/40 hover:border-azul-surelia/70"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "mensual" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-azul-surelia/15 rounded-xl p-4 shadow-sm">
              <Text className="text-sm text-azul-surelia/70">Fianzas del mes</Text>
              <Heading level={2} className="text-3xl font-bold text-azul-surelia mt-2">
                {loading ? "…" : filteredFianzas.length}
              </Heading>
            </div>
            <div className="bg-white border border-azul-surelia/15 rounded-xl p-4 shadow-sm">
              <Text className="text-sm text-azul-surelia/70">Prima total</Text>
              <Heading level={2} className="text-3xl font-bold text-azul-surelia mt-2">
                {loading
                  ? "…"
                  : new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    }).format(totalPrima)}
              </Heading>
            </div>
            <div className="bg-white border border-azul-surelia/15 rounded-xl p-4 shadow-sm">
              <Text className="text-sm text-azul-surelia/70">Comisión estimada</Text>
              <Heading level={2} className="text-3xl font-bold text-azul-surelia mt-2">
                {loading
                  ? "…"
                  : new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                    }).format(totalComision)}
              </Heading>
            </div>
          </div>

          <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-azul-surelia text-white">
                  <TableRow>
                    <TableHeader className="text-white">Cliente</TableHeader>
                    <TableHeader className="text-white">Número de Fianza</TableHeader>
                <TableHeader className="text-white">Fecha Autorización</TableHeader>
                <TableHeader className="text-white">Prima</TableHeader>
                <TableHeader className="text-white">Tarifa</TableHeader>
                <TableHeader className="text-white">Comisión</TableHeader>
                <TableHeader className="text-white">Pago</TableHeader>
                <TableHeader className="text-white">Tipo</TableHeader>
              </TableRow>
            </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan="10" className="py-8 text-center text-azul-surelia/70">
                        Cargando datos…
                      </TableCell>
                    </TableRow>
                  ) : filteredFianzas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan="10" className="py-8 text-center text-azul-surelia/70">
                        No hay fianzas con inicio de vigencia en este mes con los filtros aplicados.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredFianzas
                      .sort((a, b) => (b.inicioVigenciaMs || 0) - (a.inicioVigenciaMs || 0))
                      .map((fianza) => (
                        <TableRow
                          key={fianza.id || `${fianza.contratoId}-${fianza.numeroFianza}`}
                          className="cursor-pointer hover:bg-azul-surelia/5 transition-colors"
                          onClick={() => {
                            setSelectedFianza(fianza);
                            setIsFianzaOpen(true);
                          }}
                        >
                          <TableCell className="whitespace-nowrap">
                            <Text className="font-medium text-zinc-900">{fianza.clientName}</Text>
                          </TableCell>
                          <TableCell className="font-mono text-sm text-zinc-800">
                            {formatNumeroFianza(fianza, "N/A")}
                          </TableCell>
                      <TableCell className="text-sm text-zinc-700">
                        {fianza.inicioVigenciaMs
                          ? new Date(fianza.inicioVigenciaMs).toLocaleDateString("es-MX")
                          : "N/A"}
                      </TableCell>
                      <TableCell className="text-sm font-semibold text-zinc-900">
                        {new Intl.NumberFormat("es-MX", {
                          style: "currency",
                          currency: "MXN",
                        }).format(Number(fianza.primaAmount) || 0)}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-zinc-800">
                            {fianza.fianzaTarifa !== null
                              ? `${fianza.fianzaTarifa.toFixed(2)}%`
                              : "N/A"}
                            {fianza.primaMinima && (
                              <span className="ml-1 text-[10px] text-amber-600 font-medium">Prima mín.</span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-semibold text-azul-surelia">
                            {fianza.comisionMonto !== null
                              ? new Intl.NumberFormat("es-MX", {
                                  style: "currency",
                                  currency: "MXN",
                                }).format(fianza.comisionMonto || 0)
                              : "N/A"}
                            {fianza.comisionPct !== null && (
                              <span className="block text-xs text-azul-surelia/70">
                                {fianza.comisionPct}% sobre prima
                              </span>
                            )}
                          </TableCell>
                      <TableCell>
                        <Badge color={fianza.pagada ? "green" : "amber"}>
                          {fianza.pagada ? "Pagada" : "Pendiente"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Text className="text-sm text-zinc-700">{fianza.tipoFianza || "N/A"}</Text>
                      </TableCell>
                    </TableRow>
                  ))
              )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}

      {activeTab === "anual" && (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6 space-y-3">
          <Heading level={3} className="text-lg text-azul-surelia">Resumen anual</Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-zinc-50 rounded-lg border p-4">
              <Text className="text-sm text-azul-surelia/70">Fianzas del año</Text>
              <Heading level={4} className="text-2xl font-bold text-azul-surelia mt-1">
                {loading ? "…" : yearFiltered.length}
              </Heading>
            </div>
            <div className="bg-zinc-50 rounded-lg border p-4">
              <Text className="text-sm text-azul-surelia/70">Prima total</Text>
              <Heading level={4} className="text-2xl font-bold text-azul-surelia mt-1">
                {loading
                  ? "…"
                  : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                      yearlyTotals.prima
                    )}
              </Heading>
            </div>
            <div className="bg-zinc-50 rounded-lg border p-4">
              <Text className="text-sm text-azul-surelia/70">Comisión estimada</Text>
              <Heading level={4} className="text-2xl font-bold text-azul-surelia mt-1">
                {loading
                  ? "…"
                  : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                      yearlyTotals.comision
                    )}
              </Heading>
            </div>
          </div>

          {/* Commission Chart */}
          <div className="pt-4">
            <Text className="text-sm text-azul-surelia/70 mb-3">Comisión mensual {selectedYear}</Text>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tickFormatter={(value) =>
                    new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                      notation: "compact",
                      maximumFractionDigits: 1,
                    }).format(value)
                  }
                  tick={{ fill: '#284865', fontSize: 12 }}
                  axisLine={{ stroke: '#d1d5db' }}
                />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                  }}
                  labelStyle={{ color: '#284865', fontWeight: 600 }}
                />
                <Bar dataKey="comision" fill="#284865" radius={[4, 4, 0, 0]} name="Comisión" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="bg-azul-surelia text-white">
                  <th className="px-3 py-2 font-semibold">Mes</th>
                  <th className="px-3 py-2 font-semibold">Fianzas</th>
                  <th className="px-3 py-2 font-semibold">Prima</th>
                  <th className="px-3 py-2 font-semibold">Comisión</th>
                </tr>
              </thead>
              <tbody>
                {monthlyTotalsForYear.map((item, idx) => (
                  <tr
                    key={idx}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-zinc-50"} cursor-pointer hover:bg-azul-surelia/5`}
                    onClick={() => {
                      setSelectedMonth(idx);
                      setActiveTab("mensual");
                    }}
                  >
                    <td className="px-3 py-2 capitalize">{monthNames[idx]}</td>
                    <td className="px-3 py-2">{item.count}</td>
                    <td className="px-3 py-2">
                      {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                        item.prima
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(
                        item.comision
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={isFianzaOpen}
            onClose={() => {
              setIsFianzaOpen(false);
              setSelectedFianza(null);
            }}
            fianza={selectedFianza}
            clientData={clientsById[selectedFianza.clientId]}
            contratoData={contractsList.find((c) => c.id === selectedFianza.contratoId)}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
